import { type InputHTMLAttributes, useCallback, useRef, useState } from 'react';
import { useDatePickerFocusManager } from '@cvent/carina/components/DatePicker_NEXT/utils';

import useOnClickOutside from '@cvent/carina/hooks/useOnOutsideClick';
import type { DateRange, Matcher } from 'react-day-picker';
import Icon from '@/components/shared/Icons';
import { useBreakpoints } from '@/hooks/useBreakpoints';
import dynamic from 'next/dynamic';
import { DatePickerDialogProps } from '@cvent/carina/components/DatePicker_NEXT';
import styles from '@/components/search-bar/SearchBar.styles';

const CarinaDatePickerDialog = dynamic(() => import('@/components/shared/DatePickerDialog'), { ssr: false });

export type StringsForDateRangePicker = {
  dialogAriaLabel: string;
  prevButtonAriaLabel: string;
  nextButtonAriaLabel: string;
  guestRoomPopperResetDatesLabel: string;
  startDate: string;
  endDate: string;
};

interface Props {
  blockedDays?: Matcher[];
  inputProps: InputHTMLAttributes<HTMLInputElement>;
  handleRangeSelect?: (range: DateRange) => void;
  selectedRange?: DateRange;
  locale?: Locale;
  labels?: Record<string, string>;
  strings: StringsForDateRangePicker;
}

// TODO: replace this in favor of DateRange.tsx
export default function InputWithDatePicker({
  blockedDays,
  inputProps,
  handleRangeSelect,
  selectedRange,
  locale,
  strings
}: Props): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const popperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLInputElement | null>(null);
  const divRef = useRef<HTMLDivElement | null>(null);
  const { isDesktop } = useBreakpoints();

  const handleDismiss = useCallback(() => {
    if (isOpen) setIsOpen(false);
    setCanReturnFocusRef(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleOpen = useCallback(() => {
    if (!isOpen) setIsOpen(true);
  }, [isOpen]);

  const { handleCanReturnFocus, setCanReturnFocusRef } = useDatePickerFocusManager(isOpen, handleDismiss);

  const isStartDateDefined = selectedRange?.from !== undefined;
  const isEndDateDefined = selectedRange?.to !== undefined;

  useOnClickOutside(
    [triggerRef, popperRef],
    () => {
      setCanReturnFocusRef(true);
      handleDismiss();
    },
    isOpen
  );

  const handleInputKeyDown = useCallback(
    e => {
      if (e.key === 'Down' || e.key === 'ArrowDown') {
        if (!isOpen) {
          e.preventDefault();
          handleOpen();
        }
      }
    },
    [handleOpen, isOpen]
  );

  const handleRangeSelectInternal = (range: DateRange): void => {
    if (handleRangeSelect) {
      handleRangeSelect(range);
    }
    if (range?.to) {
      handleDismiss();
    }
  };

  const onDateChange = useCallback(
    (date: Date) => {
      if (handleRangeSelect) {
        if (inputProps?.id === 'EVENT_START_DATE') {
          handleRangeSelect({ from: date, to: selectedRange?.to });
        } else {
          handleRangeSelect({ from: selectedRange?.from, to: date });
        }
      }
    },
    [handleRangeSelect, inputProps?.id, selectedRange?.to, selectedRange?.from]
  );

  const onResetdate = useCallback(
    e => {
      e.preventDefault();
      if (handleRangeSelect) {
        if (inputProps?.id === 'EVENT_START_DATE') {
          handleRangeSelect({ from: undefined, to: selectedRange?.to });
        } else {
          handleRangeSelect({ from: selectedRange?.from, to: undefined });
        }
      }
    },
    [handleRangeSelect, inputProps?.id, selectedRange?.to, selectedRange?.from]
  );

  const dateClearButton = useCallback(
    // eslint-disable-next-line arrow-body-style
    (label: string): JSX.Element => {
      return (
        <div className=" flex w-full scale-90 cursor-pointer justify-end pt-[6px] text-b-base text-neutral-60 lg:pt-[2px]">
          <button type="button" onClick={onResetdate} aria-label={label} tabIndex={0}>
            <Icon id="x" className="h-6 w-6 fill-neutral-60" />
          </button>
        </div>
      );
    },
    [onResetdate]
  );

  const commonProps: DatePickerDialogProps = {
    defaultMonth: selectedRange?.from ?? selectedRange?.to,
    locale,
    blockedDays,
    additionalFocusTrapElements: triggerRef.current ? [triggerRef.current] : undefined,
    focusTrapOptions: {
      initialFocus: false,
      allowOutsideClick: true,
      clickOutsideDeactivates: false,
      escapeDeactivates: false,
      checkCanReturnFocus: handleCanReturnFocus
    },
    isOpen,
    numberOfMonths: 2,
    onDismiss: handleDismiss,
    triggerRef,
    popperRef,
    setCanReturnFocusRef,
    dialogAriaLabel: strings.dialogAriaLabel,
    prevButtonAriaLabel: strings.prevButtonAriaLabel,
    nextButtonAriaLabel: strings.nextButtonAriaLabel,
    onChange: onDateChange,
    mode: 'range',
    portal: true
  };

  return (
    <div className="flex" ref={divRef}>
      <input
        {...inputProps}
        value={inputProps.value || ''}
        ref={triggerRef}
        onKeyDown={handleInputKeyDown}
        onClick={handleOpen}
        onChange={e => e.preventDefault()}
        onPaste={e => e.preventDefault()}
        onFocus={() => {
          divRef.current?.classList.add('border', 'border-brand-60');
        }}
        onBlur={() => {
          divRef.current?.classList.remove('border', 'border-brand-60');
        }}
        tabIndex={0}
      />
      {isOpen &&
        (!isDesktop ? (
          <CarinaDatePickerDialog
            {...commonProps}
            onSelect={onDateChange}
            mode="single"
            selected={inputProps?.id === 'EVENT_START_DATE' ? selectedRange?.from : selectedRange?.to}
          />
        ) : (
          <CarinaDatePickerDialog {...commonProps} onSelect={handleRangeSelectInternal} selected={selectedRange} />
        ))}
      <label htmlFor={inputProps?.id} className={`${styles.dateLabel}`}>
        <span className="flex hidden cursor-pointer lg:block">
          <p className="truncate pl-7">{inputProps?.id === 'EVENT_START_DATE' ? strings.startDate : strings.endDate}</p>
        </span>

        {inputProps?.id === 'EVENT_START_DATE' &&
          isStartDateDefined &&
          dateClearButton(strings.guestRoomPopperResetDatesLabel)}
        {inputProps?.id === 'EVENT_END_DATE' &&
          isEndDateDefined &&
          dateClearButton(strings.guestRoomPopperResetDatesLabel)}
      </label>
    </div>
  );
}
